// Our own shot: recorded while the balls roll (first ball hit, pots, ball contacts), streamed to the opponent as
// sampled frames (online), and reported when everything has stopped: practice applies the rules locally, online
// sends the result to the server and waits for its state (network.ts).
//
// KNOWN BUG (not fixed here): if the shooter drops offline mid-shot, the result sent by onAllResting
// (Net.shotResult) never reaches the server. After the reconnect the shooter keeps waiting with activeShotId
// (sent in the socket handshake, socket-state.ts) and nothing resolves until the server times the shot out
// ("abandoned (timeout)"). See TESTING.md, network-drop.js.
import Balls from "../Balls";
import {GameStore} from "../features/game/game-store";
import {applyShot, Positions, ShotReport, Vec} from "../shared/rules";
import {framesMessage, Net, newShotId, shotResultMessage, shotStartMessage} from "./network";
import {frameCompleteToast} from "./hud";
import type {MatchCore, ShotInProgress} from "./types";
import type {ShotResultAck} from "./messages";

export const SAMPLE_MS = 50;   // a frame of ball positions every 50 ms (~20/s)
export const SEND_MS = 100;    // sent in batches every 100 ms

export function newShot(id: string, cueStart: Vec, now: number): ShotInProgress {
    return {id, firstHit: null, potted: [], cueStart, t0: now, lastSample: -Infinity, lastSend: now, pending: [], hits: 0};
}

/** A ball-ball contact during our shot: counts the hit and records the first object ball the cue ball touched. */
export function recordContact(shot: ShotInProgress, a: unknown, b: unknown) {
    shot.hits++;
    if (shot.firstHit === null) {
        if (a === "cue" && b) shot.firstHit = b as string;
        else if (b === "cue" && a) shot.firstHit = a as string;
    }
}

export function recordPotted(shot: ShotInProgress, id: string) {
    if (!shot.potted.includes(id)) shot.potted.push(id);
}

/** Adds a frame to the send queue if one is due (SAMPLE_MS since the last one). */
export function sampleFrame(shot: ShotInProgress, now: number, positions: () => Positions) {
    if (now - shot.lastSample < SAMPLE_MS) return;
    shot.lastSample = now;
    shot.pending.push({t: Math.round(now - shot.t0), p: positions(), h: shot.hits || undefined});
    shot.hits = 0;
}

export function sendDue(shot: ShotInProgress, now: number) {
    return now - shot.lastSend >= SEND_MS;
}

// ----- called through Match -----

/** Cue.Shot → Game.State.Shot: the cue ball has just been struck. */
export function onShotTaken(m: MatchCore) {
    const cue = Balls.byId.cue.PhysicalBody.GetPosition();
    const now = performance.now();
    const id = newShotId();
    m.activeShotId = m.mode === "online" ? id : null;
    m.shot = newShot(id, [cue.x, cue.y], now);
    m.phase = "shooting";
    GameStore.set({hint: null});
    m.publish();
}

/** Cue.SendShot: tell the opponent a shot started (online). */
export function onShotSent(m: MatchCore) {
    if (m.mode !== "online" || !m.online || !m.shot) return;
    Net.shotStart(shotStartMessage(m.online, m.shot.id, m.shot.cueStart));
}

/** Two balls touched (the physics listener, Game.PhysicsListener). */
export function onContact(m: MatchCore, a: string, b: string) {
    if (!m.shot || m.phase !== "shooting") return;
    recordContact(m.shot, a, b);
}

export function onPotted(m: MatchCore, id: string) {
    if (m.shot) recordPotted(m.shot, id);
}

/** After each frame's physics advance while shooting. */
export function afterStep(m: MatchCore) {
    if (!m.shot || m.mode !== "online") return;
    const now = performance.now();
    sampleFrame(m.shot, now, () => Balls.positions());
    if (sendDue(m.shot, now)) m.flushFrames();
}

export function flushFrames(m: MatchCore) {
    if (!m.shot || !m.online || m.shot.pending.length === 0) return;
    Net.frames(framesMessage(m.online, m.shot.id, m.shot.pending));
    m.shot.pending = [];
    m.shot.lastSend = performance.now();
}

/** Every ball has stopped after our shot. */
export function onAllResting(m: MatchCore) {
    if (!m.shot) return;
    const shot = m.shot;
    const report: ShotReport = {firstHit: shot.firstHit, potted: shot.potted, positions: Balls.positions(), cueStart: shot.cueStart};
    if (m.mode === "practice") {
        m.shot = null;
        const {state, summary} = applyShot(m.frame, report);
        // Snap the table to the rules' result, exactly like online play does with the server state:
        // restores a potted cue ball to the D (ball in hand) and re-spots potted colours.
        Balls.applyLayout(state.positions);
        m.applyFrame(state, summary);
        if (state.over) GameStore.toast(...frameCompleteToast(state));
        return;
    }
    // final positions as the last frame so the spectator ends exactly there
    shot.pending.push({t: Math.round(performance.now() - shot.t0), p: report.positions});
    m.flushFrames();
    m.shot = null;
    m.phase = "waiting";
    m.publish();
    const online = m.online!;
    Net.shotResult(shotResultMessage(online, shot.id, report), (ack: ShotResultAck) => {
        if (ack && ack.ok === false) GameStore.toast(`Shot not accepted: ${ack.error}`, "warn");
    });
    // If the server never answers (connection lost), resync when we reconnect.
}
